import { useEffect } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapStore } from '../../../stores/mapStore';

const TRAFFIC_LAYERS = ['traffic-signals-symbol', 'traffic-signals-label', 'structure-furniture-symbol'] as const;

/** Toggle OSM traffic signal tiles and combined camera/traffic poles (Coordal icons in sprite). */
export function useTrafficLayer(
  mapRef: React.MutableRefObject<MapboxMap | null>,
  trafficSignalsEnabled: boolean,
) {
  const { isMapLoaded } = useMapStore();

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapLoaded) return;

    const apply = () => {
      const activeMap = mapRef.current;
      if (!activeMap) return;
      for (const layerId of TRAFFIC_LAYERS) {
        if (!activeMap.getLayer(layerId)) continue;
        activeMap.setLayoutProperty(
          layerId,
          'visibility',
          trafficSignalsEnabled ? 'visible' : 'none',
        );
      }
      const symbolLayer = activeMap.getLayer('traffic-signals-symbol');
      if (symbolLayer) {
        // Infrastructure icons must not lose placement to Mapbox Standard label layers.
        activeMap.setLayoutProperty('traffic-signals-symbol', 'icon-allow-overlap', true);
        activeMap.setLayoutProperty('traffic-signals-symbol', 'icon-ignore-placement', true);
        activeMap.setLayoutProperty('traffic-signals-symbol', 'icon-padding', 0);
        try {
          activeMap.moveLayer('traffic-signals-symbol');
        } catch {
          // Layer may not exist yet during style transitions.
        }
      }
      const poleLayer = activeMap.getLayer('structure-furniture-symbol');
      if (poleLayer) {
        activeMap.setLayoutProperty('structure-furniture-symbol', 'icon-allow-overlap', true);
        activeMap.setLayoutProperty('structure-furniture-symbol', 'icon-ignore-placement', true);
        activeMap.setLayoutProperty('structure-furniture-symbol', 'icon-padding', 0);
        try {
          activeMap.moveLayer('structure-furniture-symbol');
        } catch {
          // Layer may not exist yet during style transitions.
        }
      }
    };

    apply();
    map.on('style.load', apply);
    return () => {
      map.off('style.load', apply);
    };
  }, [mapRef, trafficSignalsEnabled, isMapLoaded]);
}
